import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { cn } from '@/lib/utils'
import {
  ProviderUserRole,
  ProviderUserStatusCode,
  ProviderUserStatusCodeSchema,
} from '@/queries/provider-permission-management/schema'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shad/ui/form'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { zodResolver } from '@hookform/resolvers/zod'
import { MutationStatus } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Switch } from '@/shad/ui/switch'

const UserSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  role: z.string().min(1, { message: 'Role is required' }),
  status: ProviderUserStatusCodeSchema.optional(),
})

export type UserFormValues = z.infer<typeof UserSchema>

interface UserFormProps {
  defaultValues: UserFormValues | undefined
  onSubmit: (data: UserFormValues) => void
  submitButtonText?: string
  roles: ProviderUserRole[]
  mutationStatus: MutationStatus
  showStatusToggle?: boolean
}

export default function ManageUserForm({
  defaultValues,
  onSubmit,
  roles,
  mutationStatus,
  submitButtonText = 'Send Invite',
  showStatusToggle = false,
}: UserFormProps) {
  const values = defaultValues ?? {
    firstName: '',
    lastName: '',
    email: '',
    role: '',
  }

  const form = useForm<UserFormValues>({
    resolver: zodResolver(UserSchema),
    defaultValues: values,
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" {...field} className="shadow-none" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} className="shadow-none" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  {...field}
                  className="shadow-none"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role</FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="space-y-2 pt-2"
                >
                  {roles.map((role) => {
                    const isSelected = field.value === role.role_id.toString()

                    const idAsString = role.role_id.toString()
                    return (
                      <label
                        key={idAsString}
                        htmlFor={idAsString}
                        className={cn(
                          'relative block w-full cursor-pointer  transition-colors',
                          '',
                          isSelected ? '' : ' bg-white'
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem
                            value={idAsString}
                            id={idAsString}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-800 text-sm ">
                              {role.role_name}
                            </div>
                            <div className="text-sm text-gray-700">
                              {role.role_description}
                            </div>
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {showStatusToggle && (
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => {
              const currentStatus =
                field.value ?? ProviderUserStatusCode.PENDING
              const isPending = currentStatus === ProviderUserStatusCode.PENDING
              const isActive = currentStatus === ProviderUserStatusCode.ACTIVE
              const statusLabel = isPending
                ? 'Pending (not editable)'
                : isActive
                  ? 'Active'
                  : 'Inactive'

              return (
                <FormItem className="flex items-center justify-between">
                  <div>
                    <FormLabel>Status</FormLabel>
                    <div className="text-sm text-gray-600">{statusLabel}</div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={isActive}
                      disabled={isPending}
                      onCheckedChange={(checked) => {
                        field.onChange(
                          checked
                            ? ProviderUserStatusCode.ACTIVE
                            : ProviderUserStatusCode.INACTIVE
                        )
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )
            }}
          />
        )}

        <div className="flex justify-end pt-4">
          <SubmitButton
            type="submit"
            buttonTxt={submitButtonText}
            className="w-full"
            status={mutationStatus}
          />
        </div>
      </form>
    </Form>
  )
}
